import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Utils from '../../../Utils';
import Badge from 'react-bootstrap/Badge';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Table from '../../../Components/Table/Table';

export default function T25v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t25Tests, setT25Tests] = useState([]);

    const [selectedRows, setSelectedRows] = useState(t25Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;
        try {
            const { data } = await API.getTest25TestsByUserId(currentUser._id);
            if (data) {
                setT25Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            volume25: 25,
            volume400: 400,
            time25: '00:00,000',
            time400: '00:00,000',
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest25Test(currentUser._id, {
                time25Meters,
                time400Meters,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });

            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });

            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = /^([0-5]?\d):([0-5]\d),(\d{1,3})$/.exec(durationStr);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time25Meters = currentInput.time25 ? parseDuration(currentInput.time25) : 0;
    const time400Meters = currentInput.time400 ? parseDuration(currentInput.time400) : 0;

    const velocity25 = currentInput.time25 ? 25 / time25Meters : 0;
    const velocity400 = currentInput.time400 ? 400 / time400Meters : 0;

    const calculateCSS = time400Meters => (time400Meters ? (400 / time400Meters) * 0.92 : 0);
    const calculateMSS = time25Meters => (time25Meters ? 25 / time25Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time25m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 25m (sec)</div>,
            accessor: 'time25m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time25Meters}</div>
        },
        {
            id: 'time400m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 400m (sec)</div>,
            accessor: 'time400m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time400Meters}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div style={{ textAlign: 'center' }}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div style={{ textAlign: 'center' }}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div style={{ textAlign: 'center' }}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        volume25: currentInput.volume25,
        time25Meters: time25Meters,
        velocity25Meters: velocity25,
        volume400: currentInput.volume400,
        time400Meters: time400Meters,
        velocity400Meters: velocity400
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time25Meters > 0 && time400Meters > 0 ? [currentTestData] : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time25Meters, y: 25 },
                { x: row.time400Meters, y: 400 }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time25Meters > 0 && row.time400Meters > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity25Meters, y: 25 },
                    { x: row.velocity400Meters, y: 400 }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPageWrap}>
            <div className={styles.topPageProgressBarWrap + ' island'}>
                <span>
                    <b>Test Difficulty:</b>
                </span>
                <div className={styles.progressBarWrap}>
                    <ProgressBar>
                        <ProgressBar animated striped variant='success' now={30} />
                    </ProgressBar>
                </div>
            </div>
            <div className={styles.topPageWrap}>
                <div className={styles.currentTestAndSummary}>
                    <div className={styles.dataCollection + ' island'}>
                        <h2 className={styles.dataCollectionMainTitle}>Test Data</h2>
                        <h3 className={styles.dataCollectionSubTitle}>
                            Please enter your test data results.
                        </h3>
                        <form className={styles.dataCollectionForm}>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <label htmlFor='volume25'>Meters (m)</label>
                                    <input
                                        disabled={true}
                                        {...register('volume25', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='time25'>Duration (min)</label>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time25', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time25 && (
                                        <span className={styles.formInputError}>
                                            {errors.time25.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='velocity25'>Velocity (m/sec)</label>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume25,
                                            currentInput.time25
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        {...register('volume400', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time400', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time400 && (
                                        <span className={styles.formInputError}>
                                            {errors.time400.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume400,
                                            currentInput.time400
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='poolSize'>
                                    <b>Pool Size (m) </b>[For reference only]
                                </label>
                                <select className='form-control' {...register('poolSize')}>
                                    <option value={''}>-</option>
                                    <option value={25}>25m</option>
                                    <option value={50}>50m</option>
                                </select>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='testDate'>
                                    <b>Test Date & Time</b> (Defaults to now)
                                </label>
                                <input
                                    type='datetime-local'
                                    {...register('testDate')}
                                    className='form-control'
                                />
                            </div>
                            <button
                                className={`${styles.dataCollectionFormButton} btn btn-primary`}
                                onClick={handleSubmit(onSubmit)}
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                    <div className={styles.dataSummary + ' island'}>
                        <h2 className={styles.summaryTitle}>Summary</h2>
                        <div className={styles.summaryGroup}>
                            <div className={styles.summaryItems}>
                                <div className={styles.badgeGroup}>
                                    <span className={styles.currentTestHeader}>
                                        Current Test Results
                                    </span>
                                    <span className={styles.currentTestHeader}>
                                        Latest CSS & MSS for{' '}
                                        <b>{currentUser ? currentUser.firstName : ''}</b>
                                    </span>
                                </div>
                                <Badge pill bg='primary' className={styles.customCSSBadge}>
                                    Critical Swim Speed (CSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateCSS(time400Meters).toFixed(3)}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentCSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                                <Badge pill bg='primary' className={styles.customMSSBadge}>
                                    Maximum Swim Speed (MSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateMSS(time25Meters).toFixed(3) || 0}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentMSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.previousTestsWrap + ' island'}>
                    <h2 className={styles.previousTestsTitle}>Previous Tests</h2>
                    <Table
                        onRowClick={handleRowClick}
                        className={styles.previousTestsTable}
                        columns={columns}
                        data={t25Tests}
                        rowsPerPage={7}
                        updatePageSize={false}
                        pageSkipping={false}
                        selected={selectedRows}
                        showSelectionIndicator={true}
                        tableLayout='auto'
                    />
                </div>
                <div className={styles.dataInformation + ' island'}>
                    <h2 className={styles.informationTitle}>Information</h2>
                    <div className={styles.informationItems}>
                        <p>
                            This test is aimed for <b>advanced</b> or <b>intermediate</b> swimmers.
                            It takes <b>one</b> or <b>two</b> <b>days</b> to complete this test.
                            <br></br>
                            If you complete the test in <b>one</b> day, you must complete the{' '}
                            <b>25 m</b> test <b>first</b> and then, after at{' '}
                            <b>least twenty minutes</b>, the <b>400 m </b> test.
                            <br></br>
                            If we complete the test in <b>two days</b>, we should complete one test
                            in one day and the second test in the next day.
                            <br></br>
                            The two trials will start in the pool without a start.
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.bottomPageWrap}>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        className={styles.chart}
                        title={'Distance - Time'}
                        titleY={'Distance (m)'}
                        titleX={'Time (sec)'}
                        datasets={datasetsDistanceTime}
                        xAxisType='linear'
                    />
                </div>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        title={'Distance - Velocity'}
                        titleY={'Distance (m)'}
                        titleX={'Velocity (m/sec)'}
                        datasets={datasetsDistanceVelocity}
                        xAxisType='linear'
                    />
                </div>
            </div>
            <div className={styles.footerPageWrap + ' island'}>
                <span className={styles.footerPageWrapNote}>
                    For your Training Zones Limits check your&nbsp;
                </span>
                <span className={styles.footerPageWrapNoteLink}>
                    <Link to='../../dashboard' style={{ color: 'inherit', textDecoration: 'none' }}>
                        Dashboard.
                    </Link>
                </span>
            </div>
        </div>
    );
}
